import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
} from '@/shared/components';
import { toApiError, useApi, type ParkingOccupancySpotRow } from '@/shared/api';
import { ParkingReleaseRow } from './ParkingReleaseRow';
import { parkingChangeProblem } from './parkingProblemText';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyGroupId: string;
  /** Die angehakten Stellplätze mit ihren Belegungen im sichtbaren Zeitraum. */
  rows: ParkingOccupancySpotRow[];
  /** Das Raster ließ sich nicht neu laden – die Zeilen können veraltet sein. */
  stale?: boolean;
}

/**
 * Der Weg zurück. Eine Sperre liess sich bisher nur anlegen, und eine Fehlzuordnung gar nicht
 * zurücknehmen — der Platz blieb bis zum Ende ihres Zeitraums unverkäuflich, und im Portal des
 * Gastes stand eine Reservierung, die niemand gewollt hat.
 *
 * <p>Eine **bezahlte** Zeile weist der Server ab: sie ist Geld, und der Weg dafür ist die
 * Gutschrift ihrer Rechnung, die den Platz selbst wieder freigibt.</p>
 *
 * <p>Aufheben fragt je Zeile nach; eine Gast-Auswahl hebt der Server immer ganz auf.</p>
 */
export function ParkingReleaseDialog({ open, onOpenChange, propertyGroupId, rows, stale }: Props) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ id: string; text: string } | null>(null);

  const release = useMutation({
    mutationFn: (reservationId: string) => api.parking.releaseBlock(propertyGroupId, reservationId),
    // Offline sofort scheitern – sonst liefe das Aufheben Minuten später von selbst los.
    networkMode: 'always',
    onMutate: () => setFailed(null),
    onSuccess: async () => {
      setConfirming(null);
      toast.success('Belegung aufgehoben – der Platz ist wieder verkaufbar.');
      await queryClient.invalidateQueries({ queryKey: ['parking-occupancy'] });
    },
    onError: async (error, reservationId) => {
      const problem = await toApiError(error);
      const text = parkingChangeProblem(
        problem,
        'Die Belegung wurde nicht aufgehoben.',
        'aufgehoben wurde',
      );
      setConfirming(null);
      setFailed({ id: reservationId, text });
      // Ist der Dialog schon zu, sagt es der Toast.
      toast.error(text);
      // Ein Zeilenstand, der nicht mehr stimmt, kommt mit dem neu geladenen Raster.
      await queryClient.invalidateQueries({ queryKey: ['parking-occupancy'] });
    },
  });

  const eintraege = rows.flatMap((row) =>
    row.reservations.map((entry) => ({ ...entry, spotLabel: row.label })),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Belegung aufheben</DialogTitle>
          <DialogDescription>
            Im sichtbaren Zeitraum auf den angehakten Stellplätzen. Bezahlte Reservierungen stehen
            hier ohne Knopf – sie werden über die Gutschrift ihrer Rechnung aufgehoben.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {stale && (
            <p role="status" className="text-amber-ink mb-2 text-xs">
              Der Stand ließ sich nicht neu laden – die Liste kann veraltet sein.
            </p>
          )}
          {eintraege.length === 0 ? (
            <EmptyState
              icon="Pin"
              title="Nichts zu sehen"
              description="Auf den gewählten Stellplätzen liegt in diesem Zeitraum keine Belegung."
            />
          ) : (
            <ul className="divide-line/40 divide-y">
              {eintraege.map((entry) => (
                <ParkingReleaseRow
                  key={entry.reservationId}
                  entry={entry}
                  confirming={confirming === entry.reservationId}
                  busy={release.isPending}
                  problem={failed?.id === entry.reservationId ? failed.text : null}
                  onAsk={() => {
                    setFailed(null);
                    setConfirming(entry.reservationId);
                  }}
                  onCancel={() => setConfirming(null)}
                  onConfirm={() => release.mutate(entry.reservationId)}
                />
              ))}
            </ul>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" className="min-h-11" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
